// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { formatBankUploadParseError } from '@client/banks/excel/format-bank-upload-parse-error';
import type { BankUploadParseError } from '@client/banks/excel/types';
import { Button, Typography } from '@mui/material';
import { AppIcon } from '@shared/ui-kit/components';
import { IconButton } from '@shared/ui-kit/components/icon-button';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';
import { classNames } from '@shared/ui-kit/utils/classnames';
import type { ChangeEvent, DragEvent, FC, ReactNode, RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './excel-import.module.css';

export type ExcelImportProps = {
  'data-testid': string;
  title: ReactNode;
  description: ReactNode;
  stepDownloadTitle: ReactNode;
  stepDownloadDescription: ReactNode;
  stepUploadTitle: ReactNode;
  stepUploadDescription: ReactNode;
  downloadTemplateLabel: ReactNode;
  dragHereLabel: ReactNode;
  fileConstraintsLabel: ReactNode;
  selectFileLabel: ReactNode;
  processingNote: ReactNode;
  submitLabel: ReactNode;
  fileInputRef: RefObject<HTMLInputElement>;
  selectedFile: File | null;
  isDragging: boolean;
  excelAccept: string;
  hasSelectedFile: boolean;
  isParsing?: boolean;
  parseErrors?: BankUploadParseError[];
  onDownloadTemplate: () => void;
  onSelectFileClick: () => void;
  onFileInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onDragOver: (event: DragEvent<HTMLDivElement>) => void;
  onDragLeave: () => void;
  onDrop: (event: DragEvent<HTMLDivElement>) => void;
  onRemoveFile: () => void;
  onImport: () => void;
};

export const ExcelImport: FC<ExcelImportProps> = ({
  'data-testid': testId,
  title,
  description,
  stepDownloadTitle,
  stepDownloadDescription,
  stepUploadTitle,
  stepUploadDescription,
  downloadTemplateLabel,
  dragHereLabel,
  fileConstraintsLabel,
  selectFileLabel,
  processingNote,
  submitLabel,
  fileInputRef,
  selectedFile,
  isDragging,
  excelAccept,
  hasSelectedFile,
  isParsing = false,
  parseErrors = [],
  onDownloadTemplate,
  onSelectFileClick,
  onFileInputChange,
  onDragOver,
  onDragLeave,
  onDrop,
  onRemoveFile,
  onImport,
}) => {
  const { t } = useTranslation();

  return (
    <div className={styles['excel-import-step-body']} data-testid={testId}>
      <header className={styles['excel-import-header']}>
        <Typography
          component="h2"
          variant="h6"
          className={styles['excel-import-title']}
        >
          {title}
        </Typography>
        <Typography variant="body2" className={styles['excel-import-description']}>
          {description}
        </Typography>
      </header>

      <div className={styles['excel-import-steps']}>
        <section className={styles['excel-import-step']}>
          <div className={styles['excel-import-step-heading']}>
            <Typography
              component="h3"
              variant="body2"
              className={styles['excel-import-step-title']}
            >
              {stepDownloadTitle}
            </Typography>
            <Typography
              variant="body2"
              className={styles['excel-import-step-description']}
            >
              {stepDownloadDescription}
            </Typography>
          </div>
          <Button
            type="button"
            variant="outlined"
            color="secondary"
            className={styles['excel-import-download-button']}
            startIcon={<AppIcon icon="Download" size="lg" color="inherit" />}
            onClick={onDownloadTemplate}
            data-testid={`${testId}-download-template`}
          >
            {downloadTemplateLabel}
          </Button>
        </section>

        <section className={styles['excel-import-step']}>
          <div className={styles['excel-import-step-heading']}>
            <Typography
              component="h3"
              variant="body2"
              className={styles['excel-import-step-title']}
            >
              {stepUploadTitle}
            </Typography>
            <Typography
              variant="body2"
              className={styles['excel-import-step-description']}
            >
              {stepUploadDescription}
            </Typography>
          </div>

          {selectedFile ? (
            <div
              className={styles['excel-import-selected-file']}
              data-testid={`${testId}-selected-file`}
            >
              <Typography
                component="p"
                variant="body2"
                className={styles['excel-import-selected-file-name']}
                title={selectedFile.name}
              >
                {selectedFile.name}
              </Typography>
              <IconButton
                icon="Close"
                variant="icon"
                size="lg"
                color="main"
                aria-label="Delete"
                onClick={onRemoveFile}
                data-testid={`${testId}-remove-file`}
              />
            </div>
          ) : null}

          <div
            role="button"
            tabIndex={0}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();

                onSelectFileClick();
              }
            }}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onClick={onSelectFileClick}
            className={classNames(
              styles['excel-import-drop-zone'],
              isDragging && styles['excel-import-drop-zone-dragging'],
            )}
            data-testid={`${testId}-drop-zone`}
          >
            <AppIcon icon="Upload" size="lg" color="secondary" />
            <Typography
              variant="body2"
              className={styles['excel-import-drop-zone-title']}
            >
              {dragHereLabel}
            </Typography>
            <Typography
              variant="body2"
              className={styles['excel-import-drop-zone-hint']}
            >
              {fileConstraintsLabel}
            </Typography>
            <Button
              type="button"
              variant="outlined"
              color="secondary"
              className={styles['excel-import-select-button']}
              onClick={event => {
                event.stopPropagation();

                onSelectFileClick();
              }}
              data-testid={`${testId}-select-file`}
            >
              {selectFileLabel}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept={excelAccept}
              className="sr-only"
              onChange={onFileInputChange}
              data-testid={`${testId}-file-input`}
            />
          </div>
        </section>

        <Typography
          variant="body2"
          className={styles['excel-import-processing-note']}
        >
          {processingNote}
        </Typography>

        {parseErrors.length > 0 ? (
          <section
            className={styles['excel-import-errors']}
            data-testid={`${testId}-errors`}
            aria-live="polite"
          >
            <Typography
              component="h3"
              variant="body2"
              className={styles['excel-import-errors-title']}
            >
              <Translation
                i18nKey="aggregator.create-bank.excel.import.errors-title"
                ns="aggregator"
                values={{ count: parseErrors.length }}
              />
            </Typography>
            <ul className={styles['excel-import-errors-list']}>
              {parseErrors.map((error, index) => (
                <li
                  key={`${error.code}-${error.row ?? 'x'}-${error.field ?? 'x'}-${
                    error.issueKind ?? 'x'
                  }-${index}`}
                  className={styles['excel-import-errors-item']}
                >
                  {formatBankUploadParseError(error, t)}
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <Button
          type="button"
          color="secondary"
          className={styles['excel-import-submit']}
          disabled={!hasSelectedFile || isParsing}
          onClick={onImport}
          data-testid={`${testId}-submit`}
        >
          {submitLabel}
        </Button>
      </div>
    </div>
  );
};

ExcelImport.displayName = 'ExcelImport';
